import * as React from 'react';
import {
  Language as LanguageIcon,
  Twitter as TwitterIcon,
  Pinterest as PinterestIcon,
} from '@mui/icons-material';
import { Typography, Box, keyframes, styled } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { ReactComponent as IconTikTok } from '../../../../assets/social_icons/tiktok-svgrepo-com.svg';
import {
  IconFacebook,
  IconInstagram,
  IconLinkedin,
} from '../../../../utils/icons';
import { ISinglePro } from '../../../../components/singlePro/state';
import { formatProfessionsWithExperience } from '../../../../utils/professionExperience';

export const commonProps = {
  width: 12,
  size: 12,
  height: 12,
  // `stroke` lands as an SVG presentation attribute, which cannot resolve
  // var(); the token reaches those icons through currentColor instead.
  stroke: 'currentColor',
  style: {
    fill: 'var(--accent)',
    color: 'var(--accent)',
    width: 12,
    height: 12,
  },
};
export const listViewIcons = {
  instagram: <IconInstagram {...commonProps} />,
  linkedin: <IconLinkedin {...commonProps} />,
  facebook: <IconFacebook {...commonProps} />,
  tiktok: <IconTikTok {...commonProps} />,
  website: <LanguageIcon {...commonProps} />,
  twitter: <TwitterIcon {...commonProps} />,
  pinterest: <PinterestIcon {...commonProps} />,
};

export const ActionIcons = {
  View: 'view',
  Favorite: 'favorite',
  Edit: 'edit',
  Delete: 'delete',
  Copy: 'copy',
  Add: 'add',
  Link: 'link',
  Share: 'share',
};

export const listViewStatus = {
  Active: '#3DA670',
  Inactive: '#DD3024',
  Prospect: '#2474DD',
};

export const getTextOverflowProps = (lines: number) => ({
  overflow: 'hidden',
  display: '-webkit-box',
  WebkitLineClamp: lines,
  WebkitBoxOrient: 'vertical' as 'vertical',
  textOverflow: 'ellipsis',
});

export const convertInsta = (src?: string | null) => {
  if (!src) return '';
  if (/^https?:\/\//.test(src)) {
    return src;
  }
  const cleanSrc = src.replace(/@/g, '');
  return `https://www.instagram.com/${cleanSrc}`;
};

export const convertTwitter = (src?: string | null) => {
  if (!src) return '';
  if (/^https?:\/\//.test(src)) {
    return src;
  }
  const cleanSrc = src.replace(/@/g, '');
  return `https://twitter.com/${cleanSrc}`;
};

export const convertTikTok = (src?: string | null) => {
  if (!src) return '';
  if (/^https?:\/\//.test(src)) {
    return src;
  }
  return `https://www.tiktok.com/${src}`;
};

export const convertPinterest = (src?: string | null) => {
  if (!src) return '';
  if (/^https?:\/\//.test(src)) {
    return src;
  }
  return `https://pinterest.com/${src}`;
};

export const convertLinkedIn = (src?: string | null) => {
  if (!src) return '';
  if (/^https?:\/\//.test(src)) {
    return src;
  }
  return `https://www.linkedin.com/in/${src}`;
};

export const convertWebsite = (site?: string) => {
  if (!site) return '';
  if (!/^https?:\/\//.test(site)) {
    return `https://${site}`;
  }
  return site;
};

export const getHeaderText = (pro: ISinglePro) => (
  <Typography
    variant="body2"
    fontFamily="CircularStd"
    className="text-text-2"
  >
    {formatProfessionsWithExperience(pro.professions)}
  </Typography>
);

export const getExpertProfessionList = (pro: ISinglePro) => (
  <div className="w-full min-w-0">
    <Typography
      variant="body2"
      fontFamily="CircularStd"
      className="text-muted text-xs"
      style={{ ...getTextOverflowProps(1) }}
    >
      {formatProfessionsWithExperience(pro.professions)}
    </Typography>
  </div>
);

export const KeyPointList = styled((props) => <ul {...props} />)`
  margin-top: 0.5rem;

  li {
    padding-left: 15px;
    position: relative;
  }

  li::before {
    content: "•"; /* Bullet symbol */
    position: absolute;
    left: 0px;
    color: var(--text);
    font-size: 1em;
  }
`;

export const slideRight = keyframes`
  0% {
    transform: translateX(0);
    opacity: 1;
  }
  50% {
    transform: translateX(10px);
    opacity: 1;
  }
  100% {
    transform: translateX(0);
    opacity: 1;
  }
`;

export const GoToPortalLink = ({ fontSize = 16, isLoggedIn = false }: { fontSize?: number; isLoggedIn?: boolean }) => {
  const navigate = useNavigate();

  const handleClick = () => {
    if (isLoggedIn) {
      navigate('/video-production/projects');
    } else {  
      navigate('/register');
    }
  };

  return (
    <Typography
      variant="body1"
      fontSize={fontSize}
      fontFamily="CircularStd"
      color="#fff"
      sx={{
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        '&:hover': {
          opacity: 0.8,
        },
      }}
      onClick={handleClick}
    >
      {isLoggedIn ? 'Go to Dashboard' : 'Try the TDC Portal today'}
      <Box
        component="span"
        sx={{
          display: 'inline-block',
          animation: `${slideRight} 1s ease-in-out infinite`,
          marginLeft: '4px',
        }}
      >
        →
      </Box>
    </Typography>
  );
};
